import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';

type IntegrationMethod = 'oc' | 'eds' | 'react' | 'javascript';
type Environment = 'production' | 'staging';

interface IntegrationSnippetsProps {
  accountToken: string | null;
  compact?: boolean;
}

const REGISTRY_URLS = {
  production: 'https://mfe.workspace.accessacloud.com/registry',
  staging: 'https://mfe.test.workspace.accessacloud.com/registry',
};

const API_HOSTS = {
  production: 'https://api.sentiment.accessacloud.com',
  staging: 'https://api-staging.sentiment.accessacloud.com',
};

export function IntegrationSnippets({ accountToken, compact = false }: IntegrationSnippetsProps) {
  const [method, setMethod] = useState<IntegrationMethod>('oc');
  const [environment, setEnvironment] = useState<Environment>('production');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const token = accountToken || 'YOUR_ACCOUNT_TOKEN';
  const registryUrl = REGISTRY_URLS[environment];
  const apiHost = API_HOSTS[environment];

  const handleCopy = async (text: string, index: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const snippets: Record<IntegrationMethod, Array<{ label?: string; code: string; language: string }>> = {
    oc: [
      {
        label: '1. Add the OC client script to your HTML',
        code: `<script src="${registryUrl}/oc-client/client.js"></script>`,
        language: 'html',
      },
      {
        label: '2. Create a wrapper component (React example)',
        code: `import { useEffect, useRef } from 'react';

interface FeatureFeedbackProps {
  feature: string;
  product?: string;
  customerName: string;
  orgId?: string;
  userId: string;
  reDisplayAfterDays?: number;
  theme?: 'light' | 'dark';
  dismissible?: boolean;
  language?: string;
}

export function FeatureFeedback({
  feature,
  product = 'MyProduct',
  customerName,
  orgId = '',
  userId,
  reDisplayAfterDays = 30,
  theme = 'light',
  dismissible = true,
  language = 'en-GB',
}: FeatureFeedbackProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const params = new URLSearchParams({
      feature,
      product,
      accountToken: '${token}',
      apiHost: '${apiHost}',
      customerName,
      orgId, // Optional; used for reporting
      userId,
      theme,
      dismissible: String(dismissible),
      reDisplayAfterDays: String(reDisplayAfterDays),
      language,
    });

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute(
      'href',
      '${registryUrl}/evo-feature-feedback-collector/1/?' + params.toString()
    );

    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(ocComponent);

    // Render the component
    window.oc?.renderUnloadedComponents();

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [feature, product, customerName, orgId, userId, theme, dismissible, reDisplayAfterDays, language]);

  return <div ref={containerRef} />;
}`,
        language: 'tsx',
      },
      {
        label: '3. Use in your app',
        code: `// orgId is optional and used for reporting.
<FeatureFeedback
  feature="checkout-flow"
  product="MyApp"
  customerName="Acme Corp"
  orgId="YOUR_ORG_ID"
  userId="user@example.com"
  reDisplayAfterDays={30}
  theme="light"
/>`,
        language: 'tsx',
      },
      {
        label: 'Or use inline HTML (simple)',
        code: `<oc-component
  href="${registryUrl}/evo-feature-feedback-collector/1/?feature=YOUR_FEATURE&product=YOUR_PRODUCT&accountToken=${token}&apiHost=${apiHost}&customerName=YOUR_CUSTOMER&orgId=YOUR_ORG_ID&userId=YOUR_USER_ID&theme=light&reDisplayAfterDays=30">
</oc-component>`,
        language: 'html',
      },
    ],
    eds: [
      {
        label: 'Install package',
        code: 'npm install @feature-feedback/react',
        language: 'bash',
      },
      {
        label: 'Install EDS component',
        code: 'npx shadcn@latest add https://registry.eds.access-evo.com/r/user-feedback-dialog.json',
        language: 'bash',
      },
      {
        label: 'Usage',
        code: `import { useFeedbackSubmit, type FeedbackVote } from '@feature-feedback/react';
import { UserFeedbackDialog } from '@/components/blocks/user-feedback-dialog';

const feedback = useFeedbackSubmit({
  accountToken: '${token}',
  feature: 'YOUR_FEATURE',
  customerName: 'YOUR_CUSTOMER',
  orgId: 'YOUR_ORG_ID', // Optional; used for reporting
  userId: 'user@example.com',
  apiHost: '${apiHost}',
});

// In your JSX:
<UserFeedbackDialog
  pageName="YOUR_FEATURE"
  open={dialogOpen}
  onOpenChange={setDialogOpen}
  onSubmit={handleSubmit}
  includeStarRating
/>`,
        language: 'tsx',
      },
    ],
    react: [
      {
        label: 'Install package',
        code: 'npm install @feature-feedback/react',
        language: 'bash',
      },
      {
        label: 'Usage',
        code: `import { useFeedbackSubmit, type FeedbackVote } from '@feature-feedback/react';

const feedback = useFeedbackSubmit({
  accountToken: '${token}',
  feature: 'YOUR_FEATURE',
  customerName: 'YOUR_CUSTOMER',
  orgId: 'YOUR_ORG_ID', // Optional; used for reporting
  userId: 'user@example.com',
  apiHost: '${apiHost}',
});

// State
feedback.isVisible     // Should show feedback UI
feedback.isLoading     // Submission in progress
feedback.isSuccess     // Submission succeeded
feedback.isError       // Submission failed

// Actions
feedback.onSubmit(vote, comments, rating);  // Submit feedback
feedback.onDismiss();                        // Dismiss for this session`,
        language: 'tsx',
      },
    ],
    javascript: [
      {
        label: 'Install package',
        code: 'npm install @feature-feedback/api-client',
        language: 'bash',
      },
      {
        label: 'Usage',
        code: `import { submitFeedback, shouldShowSurvey, setSurveyStorage } from '@feature-feedback/api-client';

const accountToken = '${token}';
const feature = 'YOUR_FEATURE';
const apiHost = '${apiHost}';

// Check if survey should be shown (respects localStorage state)
if (shouldShowSurvey(accountToken, feature, 30)) {
  // Collect feedback from user via your UI...

  // Submit feedback
  const success = await submitFeedback(accountToken, {
    Feature: feature,
    Customer: 'YOUR_CUSTOMER',
    OrgId: 'YOUR_ORG_ID', // Optional; used for reporting
    User: 'user@example.com',
    UserRole: '',
    Vote: 'ThumbUp', // or 'ThumbDown'
    Feedback: 'Optional comments',
    Rating: 5, // 1-5 star rating (required)
  }, apiHost);

  // Record response to prevent re-showing
  if (success) {
    setSurveyStorage(accountToken, feature);
  }
}`,
        language: 'typescript',
      },
    ],
  };

  const methodLabels: Record<IntegrationMethod, { label: string; badge?: string }> = {
    oc: { label: 'OC Component', badge: 'Recommended' },
    eds: { label: 'EDS' },
    react: { label: 'React Hook' },
    javascript: { label: 'JavaScript' },
  };

  const methodDescriptions: Record<IntegrationMethod, string> = {
    oc: 'The fastest way to integrate. Built with Evo Design System, works with any framework. Shadow DOM isolation, theming, automatic re-display logic, and translations (English, Romanian). No package installation required.',
    eds: 'For React apps using Evo Design System. Requires building your own trigger UI.',
    react: 'For React apps with custom UI requirements. You handle all UI and state.',
    javascript: 'Low-level API for full control. You handle UI, state, and re-display logic.',
  };

  return (
    <div className="space-y-6">
      <Tabs value={method} onValueChange={(v) => setMethod(v as IntegrationMethod)}>
        <TabsList className="grid w-full grid-cols-4">
          {(Object.keys(methodLabels) as IntegrationMethod[]).map((m) => (
            <TabsTrigger key={m} value={m} className="gap-1.5">
              {methodLabels[m].label}
              {!compact && methodLabels[m].badge && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {methodLabels[m].badge}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4 flex items-center gap-4">
          <Label className="text-sm text-muted-foreground">Environment:</Label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="environment"
                checked={environment === 'production'}
                onChange={() => setEnvironment('production')}
                className="h-4 w-4"
              />
              <span className="text-sm">Production</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="environment"
                checked={environment === 'staging'}
                onChange={() => setEnvironment('staging')}
                className="h-4 w-4"
              />
              <span className="text-sm">Staging</span>
            </label>
          </div>
        </div>

        {(Object.keys(snippets) as IntegrationMethod[]).map((m) => (
          <TabsContent key={m} value={m} className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">{methodDescriptions[m]}</p>

            {snippets[m].map((snippet, index) => (
              <div key={index} className="space-y-2">
                {snippet.label && (
                  <Label className="text-sm font-medium">{snippet.label}</Label>
                )}
                <div className="relative">
                  <pre className="p-4 pr-12 bg-muted rounded-md text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all cx-mask">
                    {snippet.code}
                  </pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopy(snippet.code, index)}
                    className="absolute top-2 right-2 h-8 w-8"
                  >
                    {copiedIndex === index ? (
                      <Check className="h-4 w-4 text-success-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
